import { useTabState, Tab, TabList, TabPanel } from 'reakit/Tab';
import Image from 'next/image';
import urljoin from 'url-join';
import cn from 'classnames';

import USP from '@/components/USPCardRedesigned';
import Heading from '@/components/Heading';
import Button from '@/components/Button';
import Link from '@/components/link';

export type Card = {
  title: string;
  slug: string;
  cardImage: {
    alt: string;
    url: string;
  };
};

export type Tab = {
  id: string;
  title: string;
  subTitle: string;
  usps: {
    icon: JSX.Element | string;
    title: string;
    description: string;
  }[];
  partnerLink: string;
  cards?: Card[];
};

type TabsProps = {
  handlePartnerClick: () => void;
  tabs: Tab[];
};

export default function Tabs({ handlePartnerClick, tabs }: TabsProps) {
  const tab = useTabState();

  return (
    <>
      <div className="flex justify-center">
        <TabList
          {...tab}
          aria-label="Partners"
          className="inline-grid w-full md:w-auto md:grid-cols-2 p-[2px] gap-x-1 bg-white rounded shadow-elevation"
        >
          {tabs.map(({ id, title }) => (
            <Tab
              {...tab}
              key={id}
              id={id}
              className={cn(
                `w-full py-[6px] px-2 leading-6 text-cornflower rounded focus:outline-none`,
                tab.selectedId === id
                  ? `bg-faded-cornflower font-medium`
                  : `text-light-haiti hover:bg-faded-cornflower/[0.5]`,
              )}
            >
              {title}
            </Tab>
          ))}
        </TabList>
      </div>

      {tabs.map(({ subTitle, usps, id, partnerLink, cards }) => (
        <TabPanel {...tab} key={id} className="focus:outline-none">
          <h3 className="text-lg leading-normal text-center mt-10 max-w-3xl mx-auto text-zodiac">
            {subTitle}
          </h3>

          <div className="grid grid-cols-1 gap-10 lg:grid-cols-3 mt-14">
            {usps.map(({ icon, title, description }) => (
              <USP
                key={title}
                theme="light"
                icon={icon}
                title={title}
                description={description}
              />
            ))}
          </div>

          <div className="text-center mt-14">
            <Button colour="pink" onClick={handlePartnerClick}>
              Become a Partner
            </Button>
          </div>

          <Heading className="text-center mt-30" level={2}>
            Explore our Partner Network
          </Heading>
          <h3 className="text-lg leading-normal text-center mt-6 max-w-3xl mx-auto text-zodiac">
            Our implementation and ecosytem partners come from all parts of the
            globe, perfectly equipped to accelerate ambitious projects at any
            scale.
          </h3>

          <div className="grid grid-cols-1 gap-10 lg:grid-cols-3 mt-14">
            {cards?.map((card) => (
              <Link
                key={card.slug}
                href={urljoin(partnerLink, card.slug)}
                className="shadow-elevation bg-white rounded-lg border border-catskill-white"
              >
                <Image
                  className="rounded-t-lg"
                  layout="responsive"
                  src={card.cardImage.url}
                  width={372}
                  height={274}
                  alt={card.cardImage.alt}
                />

                <p className="px-6 py-5 font-bold text-2xl">{card.title}</p>
              </Link>
            ))}
          </div>

          <div className="text-center mt-14">
            <Link href={partnerLink} className="text-rose">
              See all partners
            </Link>
          </div>
        </TabPanel>
      ))}
    </>
  );
}
